import { bindActionCreators } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import { getSelectionOrder } from '~scenes/inplay/services';

import * as commonDataListEventsActions from '../actions/commonDataListEventsActions';

/**
 * React-redux bindings
 * @param {*} state the current state
 * @returns {object} props
 */
export function mapStateToProps(state) {
    return {
        events: state.commonDataListEvents.events,
        commonDataEvents: state.commonData,
        metaData: state.metaData,
    };
}

/**
 * React-redux bindings
 * @param {*} dispatch the dispatcher function
 * @returns {object} betfeed actions
 */
export function mapDispatchToProps(dispatch) {
    return bindActionCreators(commonDataListEventsActions, dispatch);
}

/**
 * Format an array of selections (from inplay_start message) into a payload for
 * /inplay-req/market_selections_ordering" endpoint
 * @param {Array} wsDataSelections the selections
 * @returns {Object} the payload
 */
function preparGetSelectionOrderPayload(wsDataSelections) {
    const selectionOrderingPayload = {
        selections: {},
        selectionSchemas: {},
    };
    wsDataSelections.forEach((selection) => {
        const { selection_schema, market_id, id } = selection;
        if (!selectionOrderingPayload.selectionSchemas[market_id]) {
            selectionOrderingPayload.selectionSchemas[market_id] = { selection_schema };
            selectionOrderingPayload.selections[market_id] = {};
        }
        selectionOrderingPayload.selections[market_id][id] = selection;
    });

    return selectionOrderingPayload;
}

/**
 * Add new inplay events to the state (from inplay_start websocket message). Returns the new state and data
 * required for websocket (un)subscriptions
 * @param {Object} state redux state
 * @param {Object} wsData websocket message
 * @returns {Object} { (Object)newState, (Set)unsubscribeEventIds, (Array)newSelectionPointers }
 */
export function addNewInplayEventsFromWs(state, wsData) {
    const {
        events: wsPpEventIds,
        inplay_events: wsPpToIpIds,
        extra_events_info: wsExtraEventsInfo,
    } = wsData;

    const newState = cloneDeep(state);
    const { events: stateEvents, selectionPointers: stateSelectionPointers } = newState;
    const unsubscribeEventIds = new Set();
    let newSelectionPointers = [];

    wsPpEventIds.forEach((id) => {
        // find event in state
        stateEvents.some((statePpEvent, index) => {
            const { event_id: statePpId } = statePpEvent;
            if (statePpId === id) {
                const ipId = wsPpToIpIds[statePpId];
                // Assumes that default market id is same for IP/PP
                const ipMarketId = wsExtraEventsInfo[ipId]?.market_id || statePpEvent.market_id;
                const newIpEvent = {
                    event_id: ipId,
                    grouped_market_ids: [],
                    market_id: ipMarketId,
                    addedFromWs: true,
                };
                unsubscribeEventIds.add(statePpId);
                // replace PP event with IP
                newState.events[index] = newIpEvent;
                //update selection pointers
                newSelectionPointers = stateSelectionPointers.filter(({ event_id: eventId }) => (eventId !== statePpId));
                // new selection pointers

                // @see AM-4687 - filter out non-footie (if we have the information)
                const wsSelections = wsExtraEventsInfo[ipId]?.selections || [];
                let sport_slug = '';
                if (wsSelections.length) {
                    sport_slug = wsSelections[0].sport_slug;
                }
                /**
                 * @todo If we are guaranteed that we get selections with the websocket message
                 * we loop over the new selections (in wsExtraEventsInfo) and can register at
                 * selection level. However https://dedsert.atlassian.net/browse/BT-10062 means
                 * we might not get selections if there is no active market when the event goes
                 * inplay. For now we'll subscribe at market level
                 * @todo AM-4687 remove this filtering when we implement PP-IP transition for other sports
                 */
                if (sport_slug === 'football') {
                    newSelectionPointers.push({
                        event_id: ipId,
                        market_id: ipMarketId,
                    });
                }

                newState.selectionPointers = newSelectionPointers;
                newState.events = stateEvents;

                return true;
            }

            return false;
        });
    });

    return { newState, unsubscribeEventIds, newSelectionPointers };
}

/**
 * Call the selection ordering endpoint for the selections in the new inplay event.
 * @param {Object} wsData the websocket data
 * @returns {Object} updated websocket data
 */
export async function getSelectionOrderingForInplayStart(wsData) {
    const getMarketSelectionsOrderingPromises = {
        eventIds: [],
        promises: [],
    };

    Object.keys(wsData.extra_events_info).forEach((ipEventId) => {
        const { selections = false } = wsData.extra_events_info[ipEventId];
        if (selections !== false) {
            const selectionOrderingPayload = preparGetSelectionOrderPayload(selections);
            getMarketSelectionsOrderingPromises.eventIds.push(ipEventId);
            getMarketSelectionsOrderingPromises.promises.push(getSelectionOrder(selectionOrderingPayload));
        }
    });

    const selectionOrder = await Promise.all(getMarketSelectionsOrderingPromises.promises);
    selectionOrder.forEach((resolved, index) => {
        const eventId = getMarketSelectionsOrderingPromises.eventIds[index];
        Object.keys(resolved).forEach((marketId) => {
            const orderedSelections = [];
            const selectionIds = resolved[marketId];
            selectionIds.forEach((selectionId, index) => {
                const wsSelections = wsData.extra_events_info[eventId].selections;
                const newSelection = wsSelections.find(({ id }) => id === selectionId);
                // @todo AM-4687 - for 3 mkt view, we are missing a selection. To fix with SportsV2
                if (newSelection) {
                    orderedSelections.push({ ...newSelection, order: index, type: newSelection.selection_type });
                }
            });
            wsData.extra_events_info[eventId].selections = orderedSelections;
        });
    });

    return wsData;
}
